import React, { useState, useEffect } from "react";
import "./styles/globals.css";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";

// Pages
import Home from "./pages/Home";
import Publications from "./pages/Publications";
import Research from "./pages/Research";
import Engagement from "./pages/Engagement";
import Experience from "./pages/Experience";

function App() {
  const [activeSection, setActiveSection] = useState("home");
  const [isAnimating, setIsAnimating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [incomingSection, setIncomingSection] = useState("");
  const [isFooterAnimating, setIsFooterAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Scroll effect for card transformation
  useEffect(() => {
    const handleScroll = () => {
      // Disable card animation on mobile for better performance
      if (isMobile) return;

      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Calculate distance from bottom
      const distanceFromBottom =
        documentHeight - (scrollPosition + windowHeight);

      // Trigger animation when within 100px of bottom (earlier than before)
      if (distanceFromBottom <= 100 && !isAnimating) {
        setIsAnimating(true);
        setIsFooterAnimating(false); // Clear any previous footer animation state
      }
      // Reset when scrolling back up
      else if (distanceFromBottom > 150 && isAnimating) {
        // Start footer exit animation
        setIsFooterAnimating(true);

        // After transition completes, reset states
        setTimeout(() => {
          setIsAnimating(false);
          setIsFooterAnimating(false);
        }, 300); // Match CSS transition duration
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAnimating, isMobile]);

  // Handle page transitions
  const handleSectionChange = (newSection: string) => {
    if (newSection === activeSection || isTransitioning) return;

    setIsTransitioning(true);
    // Clear ALL animation states immediately during page transitions
    setIsAnimating(false);
    setIsFooterAnimating(false);

    // After shrink (300ms)
    setTimeout(() => {
      setIncomingSection(newSection);

      // Complete transition after slide-up (500ms to match new animation duration)
      setTimeout(() => {
        setIsTransitioning(false);
        setIncomingSection("");
        setActiveSection(newSection);
        window.scrollTo(0, 0);
      }, 500);
    }, 300);
  };

  const renderSection = (section: string) => {
    switch (section) {
      case "home":
        return <Home setActiveSection={handleSectionChange} />;
      case "publications":
        return <Publications />;
      case "research":
        return <Research />;
      case "engagement":
        return <Engagement />;
      case "experience":
        return <Experience />;
      default:
        return <Home setActiveSection={handleSectionChange} />;
    }
  };

  // Determine wrapper classes
  const getWrapperClasses = () => {
    let classes = "page-wrapper";

    if (isAnimating && !isMobile) classes += " card-animation";
    if (isTransitioning) classes += " page-shrink";
    if (isFooterAnimating && !isMobile) classes += " footer-exit-animation";

    return classes;
  };

  return (
    <div className="App">
      <Header
        activeSection={activeSection}
        setActiveSection={handleSectionChange}
      />

      {/* Current page wrapper */}
      <div className={getWrapperClasses()}>
        <main className="main">
          <div className="container">{renderSection(activeSection)}</div>
        </main>
      </div>

      {/* Incoming page wrapper - only show when incomingSection is set */}
      {isTransitioning && incomingSection && (
        <div className="page-wrapper incoming-page">
          <main className="main">
            <div className="container">{renderSection(incomingSection)}</div>
          </main>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default App;
